import { useRef } from 'react';
import { Box, Container, Typography, Grid, alpha, Chip } from '@mui/material';
import { motion, useInView } from 'framer-motion';

const P = { main: '#878989ff', light: '#34d399', dark: '#059669' };

const EXAMPLES = [
  {
    img: '/assets/icon/donation.webp',
    badge: 'Bağış',
    title: 'Hızlı Bağış Widget\'ı',
    desc: 'Ziyaretçiler sitenizden ayrılmadan tek tıkla bağışlarını tamamlayabilir.',
    features: ['Tutar seçimi', 'Ödeme entegrasyonu', 'Kampanya bağlantısı'],
  },
  {
    img: '/assets/icon/project.webp',
    badge: 'Proje',
    title: 'Proje Listeleme Widget\'ı',
    desc: 'Aktif projeleri kart formatında sayfanıza gömin, ilerleme bilgisiyle birlikte.',
    features: ['Filtre & sıralama', 'İlerleme çubuğu', 'Detay bağlantısı'],
  },
  {
    img: '/assets/icon/event.webp',
    badge: 'Faaliyet',
    title: 'Faaliyet Alanları Widget\'ı',
    desc: 'Derneğinizin faaliyet alanlarını görsel ve açıklamalı olarak sunun.',
    features: ['İkon & görsel', 'Kısa açıklama', 'Özelleştirilebilir renk'],
  },
  {
    img: '/assets/icon/accounting.webp',
    badge: 'Banka',
    title: 'Banka Bilgileri Widget\'ı',
    desc: 'IBAN ve hesap bilgilerini panelden bir kez girin; her sayfada otomatik görünsün.',
    features: ['Çoklu hesap', 'Kopyala butonu', 'Otomatik güncelleme'],
  },
  {
    img: '/assets/icon/bell.webp',
    badge: 'Duyuru',
    title: 'Duyuru & Haber Widget\'ı',
    desc: 'Son duyuru ve haberleri güncel tutun — panelden eklenen içerik anında yansır.',
    features: ['Tarih & başlık', 'Slider modu', 'Kategori filtresi'],
  },
  {
    img: '/assets/icon/gallery.webp',
    badge: 'Hero',
    title: 'Hero / Slider Widget\'ı',
    desc: 'Anasayfa ana vitrini için tam ekran hero ve görsel slider alanları oluşturun.',
    features: ['Görsel yükleme', 'CTA butonu', 'Otomatik geçiş'],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.09 },
  }),
};

const WidgetExamplesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 8, md: 14 },
        background: 'white',
      }}
    >
      <Container maxWidth="lg">

        <motion.div variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'}>
          <Box textAlign="center" mb={{ xs: 6, md: 9 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.8rem', md: '2.4rem' },
                color: '#0f172a',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                mb: 2,
              }}
            >
              Gerçek Widget Örnekleri
            </Typography>
            <Typography sx={{
              color: '#64748b', fontSize: '1rem',
              maxWidth: 520, mx: 'auto', lineHeight: 1.75,
            }}>
              Web sitenize ekleyebileceğiniz hazır widgetlardan bazıları —
              her biri yönetim panelinden özelleştirilebilir.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={3}>
          {EXAMPLES.map((item, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <motion.div
                variants={fadeUp}
                custom={i + 1}
                initial="hidden"
                animate={isInView ? 'visible' : 'hidden'}
                style={{ height: '100%' }}
              >
                <Box
                  sx={{
                    borderRadius: 2.5, height: '100%',
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    transition: 'all 0.25s ease',
                    cursor: 'default',
                    '&:hover': {
                      borderColor: '#cbd5e1',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.07)',
                      transform: 'translateY(-3px)',
                    },
                  }}
                >
                  {/* Üst görsel alan */}
                  <Box sx={{
                    height: 100,
                    background: alpha(P.main, 0.05),
                    borderBottom: `1px solid ${alpha(P.main, 0.1)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    position: 'relative',
                  }}>
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 2.5,
                      background: alpha(P.main, 0.09),
                      border: `1px solid ${alpha(P.main, 0.18)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Box
                        component="img"
                        src={item.img}
                        alt={item.title}
                        sx={{ width: 32, height: 32, objectFit: 'contain' }}
                      />
                    </Box>
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        position: 'absolute', top: 12, right: 12,
                        background: alpha(P.main, 0.1),
                        color: P.dark,
                        fontWeight: 600, fontSize: '0.72rem',
                        border: `1px solid ${alpha(P.main, 0.2)}`,
                        height: 22,
                      }}
                    />
                  </Box>

                  {/* İçerik */}
                  <Box sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1 }}>
                    <Typography sx={{
                      fontWeight: 700, fontSize: '0.92rem',
                      color: '#0f172a', lineHeight: 1.3,
                    }}>
                      {item.title}
                    </Typography>

                    <Typography sx={{
                      fontSize: '0.82rem', color: '#64748b', lineHeight: 1.65,
                    }}>
                      {item.desc}
                    </Typography>

                    {/* Özellikler */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 'auto', pt: 1 }}>
                      {item.features.map((f, fi) => (
                        <Typography
                          key={fi}
                          sx={{
                            fontSize: '0.75rem',
                            color: '#475569',
                            background: '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            borderRadius: 1,
                            px: 1, py: 0.25,
                            lineHeight: 1.6,
                          }}
                        >
                          {f}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>

      </Container>
    </Box>
  );
};

export default WidgetExamplesSection;
